import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UploadCase from "../components/UploadCase";
import B2CAdvisor from "../components/B2CAdvisor";
import type { TrialResult } from "../lib/api";

interface ParseInfo {
  indexedChunks: number;
  totalPages: number;
  totalCharacters: number;
}

/**
 * CasePage — vollständige Fall-Analyse.
 *
 * Zwei Routen-Varianten:
 *   /app/case          — neuer Fall, Upload-Schritt sichtbar
 *   /app/case/:caseId  — existierender Fall, Mock-Jury direkt
 *
 * Layout: Upload-Sektion oben (collapsible nach Erfolg) + Mock-Jury darunter.
 */
export default function CasePage() {
  const { caseId: routeCaseId } = useParams<{ caseId?: string }>();
  const nav = useNavigate();

  const [caseId, setCaseId] = useState<string | null>(routeCaseId ?? null);
  const [caseContext, setCaseContext] = useState<string | undefined>(undefined);
  const [parseInfo, setParseInfo] = useState<ParseInfo | undefined>(undefined);
  const [prefilledResult, setPrefilledResult] = useState<TrialResult | undefined>(undefined);

  // Wenn URL einen caseId hat, sync state
  useEffect(() => {
    if (routeCaseId && routeCaseId !== caseId) {
      setCaseId(routeCaseId);
      setCaseContext(undefined);
      setParseInfo(undefined);
      setPrefilledResult(undefined);
    }
  }, [routeCaseId, caseId]);

  const handleUploaded = (id: string, info?: ParseInfo, ctx?: string) => {
    setCaseId(id);
    setParseInfo(info);
    setCaseContext(ctx);
    setPrefilledResult(undefined);
    // URL aktualisieren auf den konkreten caseId, damit Reload + Sharing funktioniert
    nav(`/app/case/${id}`, { replace: true });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Fall analysieren</h1>
        <p className="mt-2 text-slate-400 max-w-2xl">
          Beschreibe deinen Sachverhalt ausführlich, optional mit PDF (Vertrag, Rechnung, Bescheid).
          Der 50-LLM-Schwarm urteilt in 60 Sekunden, danach kannst du Rückfragen stellen.
        </p>
      </header>

      {/* Step 1: Upload */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {caseId ? "1. Fall-Eingabe" : "1. Fall im Detail anlegen"}
          </h2>
          {caseId && (
            <button
              onClick={() => {
                setCaseId(null);
                setCaseContext(undefined);
                setParseInfo(undefined);
                setPrefilledResult(undefined);
                nav("/app/case", { replace: true });
              }}
              className="text-xs text-slate-500 hover:text-slate-300 underline transition"
            >
              Neuen Fall anlegen
            </button>
          )}
        </div>
        <UploadCase onUploaded={handleUploaded} />
        {caseId && (
          <div className="mt-4 rounded-lg bg-emerald-950/40 border border-emerald-800 px-3 py-2 text-xs text-emerald-200 space-y-1">
            <p className="font-semibold">✓ Fall angelegt</p>
            {parseInfo ? (
              <p className="text-emerald-300/80">
                {parseInfo.totalPages} Seite{parseInfo.totalPages === 1 ? "" : "n"} ·{" "}
                {parseInfo.totalCharacters.toLocaleString("de-DE")} Zeichen ·{" "}
                {parseInfo.indexedChunks} Chunk{parseInfo.indexedChunks === 1 ? "" : "s"} indexiert
              </p>
            ) : (
              <p className="text-amber-300/80">
                Text-only Fall (ohne PDF). Jury läuft auf der Beschreibung.
              </p>
            )}
            <p className="font-mono text-emerald-400/60 text-[10px] pt-1">caseId: {caseId}</p>
          </div>
        )}
      </section>

      {/* Step 2: Mock Jury */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        <h2 className="text-xl font-semibold mb-1">2. Mock Jury & Empfehlung</h2>
        <p className="text-sm text-slate-400 mb-6">
          Der LLM-Schwarm simuliert Richter, Schöffen und Anwälte. Dr. Hartmann
          destilliert die 50 Stimmen zu einer klaren Empfehlung — danach kannst
          Du Rückfragen stellen.
        </p>
        {caseId ? (
          <B2CAdvisor
            key={caseId}
            caseId={caseId}
            initialCaseContext={caseContext}
            initialResult={prefilledResult}
          />
        ) : (
          <div className="rounded-lg border border-dashed border-slate-700 bg-slate-950/40 p-8 text-center text-slate-500 text-sm">
            Lege oben einen Fall an, dann läuft die Jury hier.
          </div>
        )}
      </section>
    </div>
  );
}
